import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { uniqBy } from 'lodash';
import {
  GET_QUIZ_ATTEMPT_USERS,
  GET_TEST_ATTEMPT_USERS,
} from '../../apollo/answers-view.ts';
import { ElementType } from '../../utils/enums.ts';
import { IAutocompleteOption } from '../common/autocomplete.tsx';
import { cn, getAutocompleteKey } from '../../utils/common-utils.ts';
import { useDebounce } from '../../utils/hooks.ts';

type IAttemptUserOption = {
  key: string;
  title: string;
  email: string;
};

type IProps = {
  type: ElementType;
  quizId?: number;
  testId?: number;
  courseId?: number;
  value: IAutocompleteOption[];
  onChange: (value: IAutocompleteOption[]) => void;
};

const ATTEMPT_USERS_LIMIT = 100;

const toOption = (user: {
  id: number;
  name?: string | null;
  email: string;
}): IAttemptUserOption => ({
  key: user.id.toString(),
  title: user.name || user.email,
  email: user.email,
});

export const AnswersUserSelect = ({
  type,
  quizId,
  testId,
  courseId,
  value,
  onChange,
}: IProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<number>();
  const focusFrameRef = useRef<number>();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<IAttemptUserOption[]>([]);
  const optionsRef = useRef<IAttemptUserOption[]>([]);
  const pageRef = useRef(page);
  const autoSelectedContextRef = useRef<string>();
  const debouncedSearch = useDebounce(search, 300);
  const userSearch = search ? debouncedSearch : undefined;

  const filter = useMemo(
    () => ({
      courseId,
      userSearch,
    }),
    [courseId, userSearch],
  );

  const paging = useMemo(
    () => ({
      page,
      limit: ATTEMPT_USERS_LIMIT,
    }),
    [page],
  );

  const { data: quizUsersData, loading: loadingQuizUsers } = useQuery(
    GET_QUIZ_ATTEMPT_USERS,
    {
      fetchPolicy: 'no-cache',
      skip: type !== ElementType.QUIZ || !quizId,
      variables: {
        input: { id: quizId ?? 0 },
        paging,
        filter,
      },
    },
  );

  const { data: testUsersData, loading: loadingTestUsers } = useQuery(
    GET_TEST_ATTEMPT_USERS,
    {
      fetchPolicy: 'no-cache',
      skip: type !== ElementType.TEST || !testId,
      variables: {
        input: { id: testId ?? 0 },
        paging,
        filter,
      },
    },
  );

  const attemptUsers =
    type === ElementType.QUIZ
      ? quizUsersData?.quiz?.getQuiz?.attemptUsers
      : testUsersData?.test?.getTest?.attemptUsers;

  const selectedKeys = useMemo(
    () => new Set(value.map((item) => getAutocompleteKey(item))),
    [value],
  );

  const autoSelectContext =
    courseId && ((type === ElementType.QUIZ && quizId) || (type === ElementType.TEST && testId))
      ? `${type}:${courseId}:${quizId ?? testId}`
      : undefined;

  const isLoading = loadingQuizUsers || loadingTestUsers;
  const isDisabled =
    (type === ElementType.QUIZ && !quizId) ||
    (type === ElementType.TEST && !testId);
  const hasMore = options.length < total;

  useEffect(() => {
    setSearch('');
    setPage(1);
    setTotal(0);
    setOptions([]);
    optionsRef.current = [];
    setIsOpen(false);
    autoSelectedContextRef.current = undefined;
  }, [courseId, quizId, testId, type]);

  useEffect(() => {
    setPage(1);
    setTotal(0);
    setOptions([]);
    optionsRef.current = [];
  }, [userSearch]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  const focusInput = useCallback(
    (selectText = false) => {
      if (blurTimeoutRef.current) {
        window.clearTimeout(blurTimeoutRef.current);
      }

      setIsOpen(true);
      focusFrameRef.current = window.requestAnimationFrame(() => {
        inputRef.current?.focus();

        if (selectText && inputRef.current?.value) {
          inputRef.current.select();
        }
      });
    },
    [],
  );

  useEffect(() => {
    if (!attemptUsers) return;

    const nextOptions = attemptUsers.results.map(toOption);
    const mergedOptions =
      pageRef.current === 1
        ? nextOptions
        : uniqBy([...optionsRef.current, ...nextOptions], 'key');
    const nextTotal = attemptUsers.total ?? mergedOptions.length;

    optionsRef.current = mergedOptions;
    setOptions(mergedOptions);
    setTotal(nextTotal);

    if (!autoSelectContext) return;
    if (autoSelectedContextRef.current === autoSelectContext) return;
    if (search) return;

    if (mergedOptions.length < nextTotal) {
      setPage((currentPage) => currentPage + 1);
      return;
    }

    autoSelectedContextRef.current = autoSelectContext;
    onChange(mergedOptions);
  }, [attemptUsers, autoSelectContext, onChange, search]);

  const selectUser = useCallback(
    (option: IAttemptUserOption) => {
      const isSelected = selectedKeys.has(option.key);
      const nextValue = isSelected
        ? value.filter((item) => getAutocompleteKey(item) !== option.key)
        : [...value, option];

      onChange(nextValue);
      focusInput(!!search);
    },
    [focusInput, onChange, search, selectedKeys, value],
  );

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading) return;
    setPage((currentPage) => currentPage + 1);
  }, [hasMore, isLoading]);

  const onBlur = useCallback(() => {
    blurTimeoutRef.current = window.setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
      }
    }, 150);
  }, []);

  const onFocus = useCallback(() => {
    if (blurTimeoutRef.current) {
      window.clearTimeout(blurTimeoutRef.current);
    }
    setIsOpen(true);
  }, []);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        window.clearTimeout(blurTimeoutRef.current);
      }
      if (focusFrameRef.current) {
        window.cancelAnimationFrame(focusFrameRef.current);
      }
    };
  }, []);

  return (
    <div className="w-full" ref={containerRef}>
      <div className="flex items-start gap-3">
        <div className="relative w-full">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-gray" />
          <input
            ref={inputRef}
            className="h-10 w-full rounded-lg border border-[#dfe1e5] bg-white pl-10 pr-3 text-sm leading-5 text-gray shadow-sm placeholder:text-gray placeholder:opacity-60 focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue/20 disabled:border-[#dfe1e5] disabled:bg-light-gray disabled:text-gray disabled:opacity-100 disabled:placeholder:text-gray disabled:placeholder:opacity-60"
            disabled={isDisabled}
            onBlur={onBlur}
            onChange={(event) => {
              setSearch(event.target.value);
              setIsOpen(true);
            }}
            onFocus={onFocus}
            placeholder="Select users"
            value={search}
          />

          {isOpen && !isDisabled && (
            <div className="absolute z-[11] mt-1 max-h-72 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5">
              {!options.length && !isLoading && (
                <div className="px-4 py-2 text-gray">Nothing found.</div>
              )}

              {options.map((option) => {
                const selected = selectedKeys.has(option.key);
                return (
                  <button
                    className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-light-blue"
                    key={option.key}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => selectUser(option)}
                    type="button"
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border border-gray',
                        selected && 'border-blue bg-blue text-white',
                      )}
                    >
                      {selected && <CheckIcon className="h-3 w-3" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-black">
                        {option.title}
                      </span>
                      <span className="block truncate text-xs text-gray">
                        {option.email}
                      </span>
                    </span>
                  </button>
                );
              })}

              {hasMore && (
                <button
                  className="w-full px-3 py-2 text-left text-xs font-medium text-blue hover:underline disabled:text-gray disabled:no-underline"
                  disabled={isLoading}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={loadMore}
                  type="button"
                >
                  {isLoading
                    ? 'Loading...'
                    : `Load more (${options.length}/${total})`}
                </button>
              )}
            </div>
          )}
        </div>

        {!!value.length && (
          <div className="flex h-10 min-w-[4.75rem] flex-col justify-center text-xs text-gray">
            <span>{value.length} selected</span>
            <button
              className="w-fit py-0.5 font-medium text-blue hover:underline"
              onClick={() => onChange([])}
              type="button"
            >
              Clear all
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
